/**
 * lockFn - 包装一个异步函数，使得多次调用时只返回第一次执行的 Promise
 * @example
 *
 * ```
 * const fetchData = async (id: string) => {
 *   const response = await fetch(`https://api.example.com/data/${id}`);
 *   return response.json();
 * };
 *
 * const shareFetchData = sharePromise(fetchData);
 *
 * // 以下多次调用只会执行一次网络请求
 * shareFetchData("123");
 * shareFetchData("123"); // 复用第一次的 Promise
 * shareFetchData("123"); // 复用第一次的 Promise
 *
 * ```
 *
 * @param fn 原始异步函数
 * @returns 包装后的函数
 */
export function sharePromise<T, Args extends unknown[]>(fn: (...args: Args) => Promise<T>): (...args: Args) => Promise<T> {
  let promise: Promise<T> | null = null

  return (...args: Args): Promise<T> => {
    // 如果已经有一个正在执行的 Promise，直接返回它
    if (promise) {
      return promise
    }

    // 执行原始函数并保存 Promise
    promise = fn(...args).finally(() => {
      // 当 Promise 完成（无论成功或失败）后，重置状态以允许下一次调用
      promise = null
    })

    return promise
  }
}
